import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Table, Loader, Typography, Iconography } from '@lucid/lego';
import type { FilterState } from '@lucid/lego';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import {
  useSearchVehicles,
  useGetPrCodes,
  type ModelType,
} from '../../api/services/lupp/inventory/inventory';
import type { VehicleSearchFilters } from '../../api/services/lupp/inventory/dto.inventory';
import {
  getInventoryColumns,
  DEFAULT_FILTER_STATE,
  type InventoryTableRow,
} from '../Configurator/utils';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  MIN_SEARCH_LENGTH,
  FILTER_KEY_MAPPING,
  COLUMN_TO_API_FIELD_MAPPING,
} from '../Configurator/constants';
import styles from './AvailableVehicles.module.scss';

export type AvailableVehiclesProps = {
  /**
   * Callback when a vehicle is selected (VIN or batch number click)
   */
  onVehicleSelect: (vehicle: InventoryTableRow) => void;
  /**
   * Callback when the table row selection changes
   */
  onSelectionChange?: (selected: InventoryTableRow[]) => void;
  /**
   * Whether the parent is processing a vehicle selection (shows overlay spinner)
   * @default false
   */
  isVehicleSelectLoading?: boolean;
  /**
   * Error message to display in a banner above the table
   */
  error?: string | null;
  /**
   * Custom data-testid for the component
   * @default 'available-vehicles'
   */
  dataTestId?: string;
  /**
   * Custom content rendered in the table toolbar next to the search bar
   */
  toolbarContent?: React.ReactNode;
  /**
   * VINs already in the cart — rows stay visible and are marked "In Cart" but remain selectable for removal
   */
  cartVins?: string[];
  /**
   * Increment to programmatically clear all row selections (e.g. after a remove-from-cart action)
   * @default 0
   */
  selectionResetKey?: number;
};

/**
 * Reusable component for browsing and filtering available vehicles.
 * Owns search, filter, sort, pagination state and API hooks internally.
 * @component
 * @param props - Component props
 * @param props.onVehicleSelect - Callback when a vehicle row is clicked
 * @param props.isVehicleSelectLoading - Shows overlay spinner when true
 * @param props.error - Error message banner
 * @param props.dataTestId - Test ID
 * @returns JSX.Element
 *
 * @example
 * <AvailableVehicles onVehicleSelect={(vehicle) => handleSelect(vehicle)} />
 */
const AvailableVehicles: React.FC<AvailableVehiclesProps> = ({
  onVehicleSelect,
  onSelectionChange,
  isVehicleSelectLoading = false,
  error: externalError,
  dataTestId = 'available-vehicles',
  toolbarContent,
  cartVins,
  selectionResetKey = 0,
}) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);

  const { data: labels } = useLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  useEffect(() => {
    if (selectionResetKey > 0) {
      setSelectedIndices([]);
    }
  }, [selectionResetKey]);
  const [pendingTrims, setPendingTrims] = useState<string[] | undefined>(undefined);
  const [pendingModel, setPendingModel] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const queryClient = useQueryClient();

  // Reset to first page and refetch when locale changes
  useEffect(() => {
    setCurrentPage(0);
    queryClient.invalidateQueries({ queryKey: ['VEHICLE_SEARCH'] });
  }, [locale, queryClient]);

  // Fetch prCodes for both models on initial render (Air: 2025, Gravity: 2026)
  const { data: airPrCodes } = useGetPrCodes('air', '2025');
  const { data: gravityPrCodes } = useGetPrCodes('gravity', '2026');

  // Determine current model from filter state or pending selection
  const appliedModel = filterState.model as string | undefined;
  const selectedModel = pendingModel ?? appliedModel;
  const isGravitySelected = selectedModel === 'GRAVITY';

  // Track the applied model type (for API calls - only changes when Apply is clicked)
  const isGravityApplied = appliedModel === 'GRAVITY';
  const appliedModelType: ModelType = isGravityApplied ? 'gravity' : 'air';

  // Use the appropriate prCodes based on selected model
  const prCodes = isGravitySelected ? gravityPrCodes : airPrCodes;

  // Track previous APPLIED model to detect changes
  const prevAppliedModelRef = useRef(appliedModelType);

  // Clear model-specific filters when APPLIED model changes
  useEffect(() => {
    if (prevAppliedModelRef.current !== appliedModelType) {
      prevAppliedModelRef.current = appliedModelType;
      const newModelValue = appliedModelType === 'gravity' ? 'GRAVITY' : 'AIR';
      setFilterState({ model: newModelValue });
      setPendingModel(undefined);
      setPendingTrims(undefined);
      setCurrentPage(0);
    }
  }, [appliedModelType]);

  // Convert Table filterState to API filters
  const apiFilters = useMemo((): VehicleSearchFilters | undefined => {
    const filters: VehicleSearchFilters = {};

    Object.entries(filterState).forEach(([key, value]) => {
      if (!value) return;
      const apiKey = FILTER_KEY_MAPPING[key];
      if (!apiKey) return;

      if (Array.isArray(value) && value.length > 0) {
        (filters as Record<string, string>)[apiKey] = value.join(',');
      } else if (typeof value === 'string' && value) {
        (filters as Record<string, string>)[apiKey] = value;
      }
    });

    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterState]);

  // Combine apiFilters with search value
  const combinedFilters = useMemo((): VehicleSearchFilters | undefined => {
    if (searchValue.trim()) {
      const trimmedSearch = searchValue.trim();
      if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
        return { ...apiFilters, vehicleIdentifier: trimmedSearch };
      }
    }
    return apiFilters;
  }, [apiFilters, searchValue]);

  // Build sort parameters for API
  const sortBy = useMemo(() => {
    if (!sortColumn) return undefined;
    return COLUMN_TO_API_FIELD_MAPPING[sortColumn] || sortColumn;
  }, [sortColumn]);

  // Search vehicles using the Auctus API
  const {
    data: searchResult,
    isLoading,
    error: searchError,
    isFetching,
  } = useSearchVehicles({
    page: currentPage,
    size: pageSize,
    filters: combinedFilters,
    sortBy,
    sortOrder: sortDirection,
  });

  const hasData = searchResult?.data !== undefined;

  const cartVinSet = useMemo(() => new Set(cartVins ?? []), [cartVins]);

  const tableData = useMemo(() => searchResult?.data ?? [], [searchResult]);

  // Build columns with dynamic filter options from prCodes
  const appliedTrims = filterState.trim as string[] | undefined;
  const selectedTrims = pendingTrims ?? appliedTrims;
  const columns = useMemo(
    () => getInventoryColumns(prCodes, isGravitySelected, selectedTrims, labels, locale),
    [prCodes, isGravitySelected, selectedTrims, labels, locale]
  );

  // Add VIN click handler and web link to columns
  const columnsWithClickHandlers = useMemo(() => {
    return columns.map((col) => {
      if (col.key === 'vin') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => {
            const isInCart = cartVinSet.has(record.vin);
            return (
              <div className={styles.vinCell}>
                <Typography
                  variant="body"
                  size="small"
                  theme={theme}
                  color="black"
                  onClick={isInCart ? undefined : () => onVehicleSelect(record)}
                  className={isInCart ? undefined : styles.clickableVin}
                >
                  {record.vin}
                </Typography>
                {isInCart && <span className={styles.inCartBadge}>In Cart</span>}
              </div>
            );
          },
        };
      }
      if (col.key === 'batchNumber') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => (
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color="black"
              onClick={() => onVehicleSelect(record)}
              className={styles.clickableVin}
            >
              {record.batchNumber}
            </Typography>
          ),
        };
      }
      if (col.key === 'webLink') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => {
            const webUrl = record.webLink;
            if (!webUrl) return null;
            return (
              <Box
                className={styles.webLinkIcon}
                onClick={() => window.open(webUrl, '_blank', 'noopener,noreferrer')}
              >
                <Iconography category="global" name="link" color="link" theme={theme} />
              </Box>
            );
          },
        };
      }
      return col;
    });
  }, [columns, theme, onVehicleSelect, cartVinSet]);

  const handleFilterChange = useCallback(
    (newFilterState: FilterState) => {
      setFilterState(newFilterState);
      setSelectedIndices([]);
      if (onSelectionChange) {
        onSelectionChange([]);
      }
      setPendingTrims(undefined);
      setPendingModel(undefined);
      setCurrentPage(0);
    },
    [onSelectionChange]
  );

  const handlePendingFilterChange = useCallback((pendingFilters: FilterState) => {
    const trimValue = pendingFilters.trim;
    const newPendingTrims = Array.isArray(trimValue) ? trimValue : undefined;
    setPendingTrims(newPendingTrims);

    const modelValue = pendingFilters.model;
    const newPendingModel = typeof modelValue === 'string' ? modelValue : undefined;
    setPendingModel(newPendingModel);
  }, []);

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  if (searchError) {
    return (
      <Box data-testid={`${dataTestId}-error`}>
        <Box pt={8}>{labels?.['configurator.error.loading'] ?? 'Error loading inventory data'}</Box>
      </Box>
    );
  }

  return (
    <div className={styles.inventoryBrowserContainer} data-testid={dataTestId}>
      {externalError && (
        <Box className={styles.errorBanner}>
          <Typography variant="body" size="small" color="white">
            {externalError}
          </Typography>
        </Box>
      )}
      <div className={styles.tableWrapper}>
        {isLoading && !hasData ? (
          <Box className={styles.loaderWrapper}>
            <Loader theme={theme} />
          </Box>
        ) : (
          <div className={styles.tableContentWrapper}>
            {(isFetching || isVehicleSelectLoading) && (
              <div className={styles.fetchingOverlay}>
                <Loader theme={invertedTheme} />
              </div>
            )}
            <Table
              columns={columnsWithClickHandlers}
              data={tableData}
              theme={invertedTheme}
              selection={{
                enabled: true,
                rows: selectedIndices,
                showExport: false,
                onChange: (indices) => {
                  setSelectedIndices(indices);
                  if (onSelectionChange) {
                    const pageOffset = currentPage * pageSize;
                    onSelectionChange(
                      indices.map((i) => tableData[i - pageOffset]).filter(Boolean)
                    );
                  }
                },
              }}
              columnConfig={{ enabled: true }}
              filter={{
                enabled: true,
                state: filterState,
                onChange: handleFilterChange,
                onPendingChange: handlePendingFilterChange,
                dependentFilters: {
                  model: [
                    'trim',
                    'exteriorColor',
                    'interiorColor',
                    'wheels',
                    'appearance',
                    'adas',
                    'packages',
                  ],
                  trim: [
                    'exteriorColor',
                    'interiorColor',
                    'wheels',
                    'appearance',
                    'adas',
                    'packages',
                  ],
                },
              }}
              pagination={{
                enabled: true,
                pageSize: pageSize,
                current: currentPage,
                total: searchResult?.totalElements ?? 0,
                onChange: (page) => {
                  setCurrentPage(page);
                  setSelectedIndices([]);
                  if (onSelectionChange) {
                    onSelectionChange([]);
                  }
                },
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(0);
                },
              }}
              search={{
                placeholder:
                  labels?.['configurator.search.placeholder'] ?? 'Search with VIN or Batch Number',
                value: searchValue,
                onChange: (value) => {
                  setSearchValue(value);
                  setCurrentPage(0);
                  if (value === '') {
                    queryClient.invalidateQueries({ queryKey: ['VEHICLE_SEARCH'] });
                  }
                },
              }}
              sort={{
                column: sortColumn,
                direction: sortDirection,
                onSort: (column, direction) => {
                  setSortColumn(column);
                  setSortDirection(direction);
                  setCurrentPage(0);
                },
              }}
              emptyState={{
                message:
                  labels?.['configurator.emptyState.message'] ?? 'No vehicles match your filters',
              }}
              labels={{
                manageColumns: labels?.['configurator.table.manageColumns'] ?? 'Manage Columns',
                filter: labels?.['configurator.table.filter'] ?? 'Filter',
                filterWithCount:
                  labels?.['configurator.table.filterWithCount'] ?? 'Filter ({{count}})',
                selectAll: labels?.['configurator.table.selectAll'] ?? 'Select All',
                clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All',
                columnsVisible:
                  labels?.['configurator.table.columnsVisible'] ??
                  '{{visible}} of {{total}} columns visible',
                showingItems:
                  labels?.['configurator.table.showingItems'] ??
                  'Showing {{start}}-{{end}} of {{total}} Items',
                pageOf: labels?.['configurator.table.pageOf'] ?? '{{current}} of {{total}}',
                save: labels?.['configurator.table.save'] ?? 'Save',
                apply: labels?.['configurator.table.apply'] ?? 'Apply',
                filtersTitle: labels?.['configurator.table.filtersTitle'] ?? 'Filters',
              }}
              toolbar={toolbarContent ? { content: toolbarContent } : undefined}
              gaComponent={`${dataTestId}-table`}
              dataTestId={`${dataTestId}-table`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailableVehicles;
